import React, { useMemo, useState } from 'react';
import { BellRing, CheckCircle2, Pencil, Save } from 'lucide-react';
import { SupportedCurrency } from '../types';
import { getCurrencySymbol } from '../utils/currency';

export interface DividendPayoutDraft {
    notificationId: string;
    symbol: string;
    name: string;
    date: string;
    quantity: number;
    amount: number;
    perShare: number;
    currency: SupportedCurrency;
}

interface DividendPayoutConfirmationsProps {
    payouts: DividendPayoutDraft[];
    onConfirm: (draft: DividendPayoutDraft) => void;
}

const roundToDecimals = (value: number, decimals: number) => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
};

const roundMoney = (value: number) => roundToDecimals(value, 2);
const roundPerShare = (value: number) => roundToDecimals(value, 4);
const formatAmountInput = (value: number) => roundMoney(value).toFixed(2);
const formatPerShareInput = (value: number) => roundPerShare(value).toFixed(4);

const DividendPayoutConfirmations: React.FC<DividendPayoutConfirmationsProps> = ({ payouts, onConfirm }) => {
    const [edits, setEdits] = useState<Record<string, { amount: string; perShare: string }>>({});

    const sortedPayouts = useMemo(
        () => [...payouts].sort((a, b) => a.date.localeCompare(b.date) || a.symbol.localeCompare(b.symbol)),
        [payouts]
    );

    if (!sortedPayouts.length) return null;

    return (
        <div className="bg-[#1e1e26] p-6 rounded-2xl border border-indigo-500/30 shadow-lg space-y-4">
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-indigo-500/15 text-indigo-300 flex items-center justify-center">
                    <BellRing size={18} />
                </div>
                <div>
                    <h3 className="font-bold text-slate-100">Dividend payouts to confirm</h3>
                    <p className="text-xs text-slate-400">Generated daily by backend scheduler. Edit and save when the cash lands in your account.</p>
                </div>
            </div>

            <div className="grid gap-4">
                {sortedPayouts.map((payout) => {
                    const currencySymbol = getCurrencySymbol(payout.currency);
                    const editState = edits[payout.notificationId];
                    const amountValue = editState?.amount ?? formatAmountInput(payout.amount);
                    const perShareValue = editState?.perShare ?? formatPerShareInput(payout.perShare);

                    return (
                        <div key={payout.notificationId} className="rounded-xl border border-white/10 bg-[#13131a] p-4">
                            <div className="flex items-start justify-between gap-4 mb-3">
                                <div>
                                    <p className="text-xs uppercase tracking-[0.16em] text-indigo-300">Payment date: {payout.date}</p>
                                    <h4 className="text-base font-semibold text-white mt-1">{payout.symbol} · {payout.name}</h4>
                                    <p className="text-sm text-slate-400 mt-1">
                                        {payout.quantity.toLocaleString()} shares × {currencySymbol}
                                        {payout.perShare.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-slate-400">Estimated total</p>
                                    <p className="text-xl font-bold text-emerald-400">
                                        {currencySymbol}
                                        {payout.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <label className="text-sm text-slate-300">
                                    <span className="inline-flex items-center gap-1 mb-1"><Pencil size={12} /> Total received</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={amountValue}
                                        onChange={(event) =>
                                            setEdits((prev) => ({
                                                ...prev,
                                                [payout.notificationId]: {
                                                    amount: event.target.value,
                                                    perShare: prev[payout.notificationId]?.perShare ?? perShareValue,
                                                },
                                            }))
                                        }
                                        onBlur={() =>
                                            setEdits((prev) => ({
                                                ...prev,
                                                [payout.notificationId]: {
                                                    amount: formatAmountInput(Number(amountValue) || 0),
                                                    perShare: prev[payout.notificationId]?.perShare ?? perShareValue,
                                                },
                                            }))
                                        }
                                        className="w-full rounded-lg border border-white/10 bg-[#0f0f16] px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/60"
                                    />
                                </label>
                                <label className="text-sm text-slate-300">
                                    <span className="inline-flex items-center gap-1 mb-1"><Pencil size={12} /> Per share</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.0001"
                                        value={perShareValue}
                                        onChange={(event) =>
                                            setEdits((prev) => ({
                                                ...prev,
                                                [payout.notificationId]: {
                                                    amount: prev[payout.notificationId]?.amount ?? amountValue,
                                                    perShare: event.target.value,
                                                },
                                            }))
                                        }
                                        onBlur={() =>
                                            setEdits((prev) => ({
                                                ...prev,
                                                [payout.notificationId]: {
                                                    amount: prev[payout.notificationId]?.amount ?? amountValue,
                                                    perShare: formatPerShareInput(Number(perShareValue) || 0),
                                                },
                                            }))
                                        }
                                        className="w-full rounded-lg border border-white/10 bg-[#0f0f16] px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/60"
                                    />
                                </label>
                            </div>

                            <div className="flex items-center justify-between">
                                <p className="text-xs text-slate-400">Edit either value and confirm. The payout will be saved permanently.</p>
                                <button
                                    onClick={() => {
                                        const amount = Math.max(Number(amountValue) || 0, 0);
                                        const perShare = Math.max(roundPerShare(Number(perShareValue) || 0), 0);
                                        if (amount <= 0 && perShare <= 0) return;
                                        onConfirm({
                                            ...payout,
                                            amount: amount > 0 ? roundMoney(amount) : roundMoney(perShare * payout.quantity),
                                            perShare: perShare > 0 ? perShare : roundPerShare(amount / payout.quantity),
                                        });
                                    }}
                                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 transition-colors"
                                >
                                    <Save size={14} />
                                    Confirm & save
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="text-xs text-slate-500 inline-flex items-center gap-2">
                <CheckCircle2 size={13} className="text-emerald-400" />
                Confirmed payouts are persisted as dividend transactions and marked complete.
            </div>
        </div>
    );
};

export default DividendPayoutConfirmations;
